/**
 * MEVIA - Company Identity Profile
 * Phase 4: Form for company admin to capture mission, vision, values
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  ArrowLeft,
  Save,
  Send,
  Plus,
  Trash2,
  RefreshCw,
  Building2,
  Target,
  Eye,
  Lightbulb,
  Heart,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import logger from '../utils/logger';

// Status configuration
const STATUS_CONFIG = {
  draft: {
    es: 'Borrador',
    en: 'Draft',
    color: 'bg-gray-100 text-gray-800',
    icon: FileText,
    description: {
      es: 'Puedes editar la información antes de enviar',
      en: 'You can edit the information before submitting'
    }
  },
  submitted: {
    es: 'Enviado',
    en: 'Submitted',
    color: 'bg-amber-100 text-amber-800',
    icon: Clock,
    description: {
      es: 'Esperando que el equipo de soporte genere tu perfil',
      en: 'Waiting for support team to generate your profile'
    }
  },
  active: {
    es: 'Activo',
    en: 'Active',
    color: 'bg-green-100 text-green-800',
    icon: CheckCircle,
    description: {
      es: 'Tu perfil personalizado está activo',
      en: 'Your custom profile is active'
    }
  },
  inactive: {
    es: 'Inactivo',
    en: 'Inactive',
    color: 'bg-gray-100 text-gray-600',
    icon: Lock,
    description: {
      es: 'El perfil está desactivado',
      en: 'Profile is disabled'
    }
  }
};

const CompanyIdentityProfile = ({ onBack }) => {
  const { language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [identityData, setIdentityData] = useState(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitNotes, setSubmitNotes] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    mission: '',
    vision: '',
    values: [''],
    optional_blocks: []
  });

  // Translations
  const t = {
    title: language === 'es' ? 'Identidad Corporativa' : 'Corporate Identity',
    subtitle: language === 'es' 
      ? 'Define la misión, visión y valores de tu empresa para generar un perfil personalizado'
      : 'Define your company\'s mission, vision, and values to generate a custom profile',
    back: language === 'es' ? 'Volver' : 'Back',
    mission: language === 'es' ? 'Misión' : 'Mission',
    missionPlaceholder: language === 'es' 
      ? '¿Cuál es el propósito fundamental de tu empresa? ¿Qué problema resuelve?'
      : 'What is your company\'s fundamental purpose? What problem does it solve?',
    vision: language === 'es' ? 'Visión' : 'Vision',
    visionPlaceholder: language === 'es'
      ? '¿Cómo te imaginas a tu empresa en el futuro? ¿Qué impacto quieres lograr?'
      : 'How do you envision your company in the future? What impact do you want to achieve?',
    values: language === 'es' ? 'Valores' : 'Values',
    valuesDesc: language === 'es'
      ? 'Los principios que guían el comportamiento de tu organización'
      : 'The principles that guide your organization\'s behavior',
    addValue: language === 'es' ? 'Agregar Valor' : 'Add Value',
    valuePlaceholder: language === 'es' ? 'Ej: Integridad, Innovación...' : 'E.g.: Integrity, Innovation...',
    optionalBlocks: language === 'es' ? 'Información Adicional (Opcional)' : 'Additional Information (Optional)',
    addBlock: language === 'es' ? 'Agregar Bloque' : 'Add Block',
    blockTitle: language === 'es' ? 'Título' : 'Title',
    blockContent: language === 'es' ? 'Contenido' : 'Content',
    save: language === 'es' ? 'Guardar Borrador' : 'Save Draft',
    saving: language === 'es' ? 'Guardando...' : 'Saving...',
    submit: language === 'es' ? 'Enviar para Generar Perfil' : 'Submit for Profile Generation',
    submitTitle: language === 'es' ? 'Enviar Identidad' : 'Submit Identity',
    submitDesc: language === 'es'
      ? 'Al enviar, el equipo de soporte generará un perfil cultural basado en tu identidad corporativa. Una vez activo, este perfil reemplazará la evaluación por tokens.'
      : 'By submitting, the support team will generate a cultural profile based on your corporate identity. Once active, this profile will replace the token-based assessment.',
    notesForSupport: language === 'es' ? 'Notas para soporte (opcional)' : 'Notes for support (optional)',
    notesPlaceholder: language === 'es' 
      ? 'Información adicional que quieras compartir con el equipo...'
      : 'Additional information you want to share with the team...',
    cancel: language === 'es' ? 'Cancelar' : 'Cancel',
    confirm: language === 'es' ? 'Confirmar Envío' : 'Confirm Submission',
    status: language === 'es' ? 'Estado' : 'Status',
    profileEnabled: language === 'es' ? 'Perfil por Identidad Activo' : 'Identity Profile Active',
    profileDisabled: language === 'es' ? 'Usando Evaluación por Tokens' : 'Using Token Assessment',
    savedSuccess: language === 'es' ? 'Borrador guardado' : 'Draft saved',
    submittedSuccess: language === 'es' ? 'Identidad enviada. El equipo de soporte te contactará pronto.' : 'Identity submitted. Support team will contact you soon.',
    error: language === 'es' ? 'Error al procesar la solicitud' : 'Error processing request',
    required: language === 'es' ? 'Campo requerido' : 'Required field',
    minValues: language === 'es' ? 'Agrega al menos un valor' : 'Add at least one value',
    cannotEdit: language === 'es' 
      ? 'Este perfil está activo y no puede ser modificado. Contacta a soporte si necesitas cambios.'
      : 'This profile is active and cannot be modified. Contact support if you need changes.'
  };

  const loadIdentity = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/company-identity/my-identity');
      setIdentityData(response.data);
      
      if (response.data.exists) {
        setFormData({
          mission: response.data.mission || '',
          vision: response.data.vision || '',
          values: response.data.values?.length > 0 ? response.data.values : [''],
          optional_blocks: response.data.optional_blocks || []
        });
      }
    } catch (error) {
      logger.error('Error loading identity:', error);
      if (error.response?.status !== 404) {
        toast.error(t.error);
      }
    } finally {
      setLoading(false);
    }
  }, [t.error]);

  useEffect(() => {
    loadIdentity();
  }, [loadIdentity]);

  const handleSave = async () => {
    // Validate
    if (!formData.mission.trim() || !formData.vision.trim()) {
      toast.error(t.required);
      return;
    }
    
    const nonEmptyValues = formData.values.filter(v => v.trim());
    if (nonEmptyValues.length === 0) {
      toast.error(t.minValues);
      return;
    }
    
    try {
      setSaving(true);
      await axios.post('/company-identity/my-identity', {
        mission: formData.mission,
        vision: formData.vision,
        values: nonEmptyValues,
        optional_blocks: formData.optional_blocks.filter(b => b.title.trim() && b.content.trim())
      });
      
      toast.success(t.savedSuccess);
      loadIdentity();
    } catch (error) {
      logger.error('Error saving identity:', error);
      toast.error(error.response?.data?.detail || t.error);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await axios.post('/company-identity/my-identity/submit', {
        notes_for_support: submitNotes || null
      });
      
      toast.success(t.submittedSuccess);
      setShowSubmitDialog(false);
      setSubmitNotes('');
      loadIdentity();
    } catch (error) {
      logger.error('Error submitting identity:', error);
      toast.error(error.response?.data?.detail || t.error);
    } finally {
      setSubmitting(false);
    }
  };

  const addValue = () => {
    setFormData(prev => ({
      ...prev,
      values: [...prev.values, '']
    }));
  };

  const removeValue = (index) => {
    if (formData.values.length > 1) {
      setFormData(prev => ({
        ...prev,
        values: prev.values.filter((_, i) => i !== index)
      }));
    }
  };

  const updateValue = (index, value) => {
    setFormData(prev => ({
      ...prev,
      values: prev.values.map((v, i) => i === index ? value : v)
    }));
  };

  const addOptionalBlock = () => {
    setFormData(prev => ({
      ...prev,
      optional_blocks: [...prev.optional_blocks, { title: '', content: '' }]
    }));
  };

  const removeOptionalBlock = (index) => {
    setFormData(prev => ({
      ...prev,
      optional_blocks: prev.optional_blocks.filter((_, i) => i !== index)
    }));
  };

  const updateOptionalBlock = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      optional_blocks: prev.optional_blocks.map((block, i) => 
        i === index ? { ...block, [field]: value } : block
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const status = identityData?.exists ? identityData.status : null;
  const StatusIcon = status ? STATUS_CONFIG[status]?.icon : null;
  const isEditable = !status || status === 'draft' || status === 'inactive';
  const canSubmit = status === 'draft' && formData.mission.trim() && formData.vision.trim() && formData.values.some(v => v.trim());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t.back}
            </Button>
          )}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="h-6 w-6 text-teal-600" />
              {t.title}
            </h2>
            <p className="text-sm text-gray-500">{t.subtitle}</p>
          </div>
        </div>
        
        {/* Status Badge */}
        {status && (
          <div className="flex items-center gap-2">
            <Badge className={`${STATUS_CONFIG[status]?.color} flex items-center gap-1 px-3 py-1`}>
              {StatusIcon && <StatusIcon className="h-4 w-4" />}
              {STATUS_CONFIG[status]?.[language]}
            </Badge>
            {identityData?.identity_profile_enabled && (
              <Badge className="bg-teal-100 text-teal-800">
                {t.profileEnabled}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Status Message */}
      {status && status !== 'draft' && (
        <Card className={`${
          status === 'submitted' ? 'border-amber-200 bg-amber-50' :
          status === 'active' ? 'border-green-200 bg-green-50' :
          'border-gray-200 bg-gray-50'
        }`}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              {StatusIcon && <StatusIcon className={`h-5 w-5 ${
                status === 'submitted' ? 'text-amber-600' :
                status === 'active' ? 'text-green-600' :
                'text-gray-600'
              }`} />}
              <p className={`${
                status === 'submitted' ? 'text-amber-800' :
                status === 'active' ? 'text-green-800' :
                'text-gray-800'
              }`}>
                {STATUS_CONFIG[status]?.description[language]}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cannot Edit Warning */}
      {!isEditable && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <p className="text-amber-800">{t.cannotEdit}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mission */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-teal-600" />
            {t.mission} *
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={formData.mission}
            onChange={(e) => setFormData(prev => ({ ...prev, mission: e.target.value }))}
            placeholder={t.missionPlaceholder}
            disabled={!isEditable}
            className="min-h-[120px]"
          />
        </CardContent>
      </Card>

      {/* Vision */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-purple-600" />
            {t.vision} *
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={formData.vision}
            onChange={(e) => setFormData(prev => ({ ...prev, vision: e.target.value }))}
            placeholder={t.visionPlaceholder}
            disabled={!isEditable}
            className="min-h-[120px]"
          />
        </CardContent>
      </Card>

      {/* Values */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-red-500" />
                {t.values} *
              </CardTitle>
              <CardDescription>{t.valuesDesc}</CardDescription>
            </div>
            {isEditable && (
              <Button variant="outline" size="sm" onClick={addValue}>
                <Plus className="h-4 w-4 mr-1" />
                {t.addValue}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {formData.values.map((value, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-sm font-medium text-teal-700">
                  {index + 1}
                </div>
                <Input
                  value={value}
                  onChange={(e) => updateValue(index, e.target.value)}
                  placeholder={t.valuePlaceholder}
                  disabled={!isEditable}
                  className="flex-1"
                />
                {isEditable && formData.values.length > 1 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => removeValue(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Optional Blocks */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-amber-500" />
                {t.optionalBlocks}
              </CardTitle>
            </div>
            {isEditable && (
              <Button variant="outline" size="sm" onClick={addOptionalBlock}>
                <Plus className="h-4 w-4 mr-1" />
                {t.addBlock}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {formData.optional_blocks.length === 0 ? (
            <p className="text-center text-gray-500 py-4">
              {language === 'es' 
                ? 'No hay bloques adicionales. Agrega información como cultura organizacional, competencias clave, etc.'
                : 'No additional blocks. Add information like organizational culture, key competencies, etc.'}
            </p>
          ) : (
            <div className="space-y-4">
              {formData.optional_blocks.map((block, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <Input
                      value={block.title}
                      onChange={(e) => updateOptionalBlock(index, 'title', e.target.value)}
                      placeholder={t.blockTitle}
                      disabled={!isEditable}
                      className="flex-1 font-medium"
                    />
                    {isEditable && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeOptionalBlock(index)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <Textarea
                    value={block.content}
                    onChange={(e) => updateOptionalBlock(index, 'content', e.target.value)}
                    placeholder={t.blockContent}
                    disabled={!isEditable}
                    className="min-h-[80px]"
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {isEditable && (
        <div className="flex items-center justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saving ? t.saving : t.save}
          </Button>
          
          {canSubmit && (
            <Button 
              onClick={() => setShowSubmitDialog(true)}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Send className="h-4 w-4 mr-2" />
              {t.submit}
            </Button>
          )}
        </div>
      )}

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.submitTitle}</DialogTitle>
            <DialogDescription>{t.submitDesc}</DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>{t.notesForSupport}</Label>
            <Textarea
              value={submitNotes}
              onChange={(e) => setSubmitNotes(e.target.value)}
              placeholder={t.notesPlaceholder}
              className="mt-2"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {submitting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {t.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyIdentityProfile;
